import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import type { AgentView } from '@/features/agents/types'
import { useAppSelector } from '@/store'

import {
  selectAgentSettingsVersions,
  selectLatestPublishedVersion,
  selectSelectedDraft,
} from '../slices/agentSettingsSlice'
import type {
  AgentSettingsFeatureError,
  AgentSettingsKey,
  AgentSettingsVersion,
  ConnectorPolicyView,
} from '../types'
import { useAgentSettingsLabels } from '../agentSettingsLabels'
import { AgentContextPolicyPanel } from './AgentContextPolicyPanel'
import { AgentDryRunPanel } from './AgentDryRunPanel'
import { AgentSettingsForm } from './AgentSettingsForm'
import { AgentVersionHistory } from './AgentVersionHistory'

export interface AgentGuardrailsEditorProps {
  settingsKey: AgentSettingsKey
  agent: AgentView
  policy: ConnectorPolicyView | null
  policyLoading: boolean
  policyError: AgentSettingsFeatureError | null
  onPolicyRetry: () => void
}

const immutableProvenance = (version: AgentSettingsVersion | null): string => {
  if (version === null) return '—'
  const values: ReadonlyArray<readonly [string, unknown]> = [
    ['autonomyRung', version.autonomyRung],
    ['costCeilingPerRunMinor', version.costCeilingPerRunMinor],
    ['costCeilingPerPhaseMinor', version.costCeilingPerPhaseMinor],
    ['currency', version.currency],
    ['policyPresetKey', version.policyPresetKey],
    ['reviewPolicy', version.reviewPolicy],
    ['clientOverrideReason', version.clientOverrideReason],
    ['outputPermissions', version.outputPermissions],
  ]
  return values
    .map(
      ([field, value]) =>
        `${field}:${value === undefined ? 'INHERITED' : version.scope}`
    )
    .join(', ')
}

const GRID_CLASS =
  'mt-3 grid grid-cols-[minmax(7rem,max-content)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm'

/** One visibly labelled row of a comparison card. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  )
}

/** Keeps mutable draft, immutable version, policy projection, and publish gate distinct. */
export function AgentGuardrailsEditor({
  settingsKey,
  agent,
  policy,
  policyLoading,
  policyError,
  onPolicyRetry,
}: AgentGuardrailsEditorProps) {
  const { t } = useTranslation('agents')
  const draft = useAppSelector((state) =>
    selectSelectedDraft(state, settingsKey)
  )
  const latest = useAppSelector((state) =>
    selectLatestPublishedVersion(state, settingsKey)
  )
  const versions = useAppSelector((state) =>
    selectAgentSettingsVersions(state, settingsKey)
  )
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false)
  const labels = useAgentSettingsLabels()
  const empty = t('guardrails.states.empty')

  const money = (
    currency: string | undefined,
    perRun: number | undefined,
    perPhase: number | undefined
  ): string =>
    t('guardrails.draftPublished.moneyValue', {
      currency: currency ?? empty,
      perRun: perRun ?? empty,
      perPhase: perPhase ?? empty,
    })
  const outputs = (values: readonly string[] | undefined): string =>
    values === undefined
      ? t('labels.provenance.INHERITED')
      : values.length === 0
        ? empty
        : values.map(labels.outputPermission).join(', ')

  return (
    <>
      <section className="mt-5" data-testid="agent-guardrails-editor">
        <SectionLabel>{t('guardrails.draftPublished.title')}</SectionLabel>
        <div className="mt-2 grid gap-4 lg:grid-cols-2">
          <div
            className="min-w-0 rounded-lg border border-border bg-card p-4"
            data-testid="agent-guardrails-draft"
          >
            <p className="text-sm font-medium">
              {t('guardrails.draftPublished.draft')}
            </p>
            {draft === null ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {t('guardrails.draftPublished.emptyDraft')}
              </p>
            ) : (
              <dl
                className={GRID_CLASS}
                data-testid="agent-guardrails-draft-values"
              >
                <Row label={t('guardrails.draftPublished.version')}>
                  {t('configuration.draftVersion', {
                    version: draft.draftVersion,
                  })}
                </Row>
                <Row label={t('guardrails.draftPublished.scope')}>
                  {labels.scope(draft.scope)} · {t(`phases.${draft.phase}`)}
                </Row>
                <Row label={t('guardrails.draftPublished.autonomy')}>
                  {draft.autonomyRung === undefined
                    ? t('labels.provenance.INHERITED')
                    : labels.rung(draft.autonomyRung)}
                </Row>
                <Row label={t('guardrails.draftPublished.money')}>
                  {money(
                    draft.currency,
                    draft.costCeilingPerRunMinor,
                    draft.costCeilingPerPhaseMinor
                  )}
                </Row>
                <Row label={t('guardrails.draftPublished.review')}>
                  <span data-testid="agent-guardrails-draft-review">
                    {labels.reviewPolicy(draft.reviewPolicy)}
                  </span>
                </Row>
                <Row label={t('guardrails.draftPublished.outputs')}>
                  {outputs(draft.outputPermissions)}
                </Row>
                <Row label={t('guardrails.draftPublished.reason')}>
                  {draft.clientOverrideReason ?? empty}
                </Row>
                <Row label={t('guardrails.draftPublished.configuredBy')}>
                  {draft.configuredByName ?? draft.configuredBy ?? empty}
                </Row>
                <Row label={t('guardrails.draftPublished.configuredAt')}>
                  <time
                    dateTime={
                      draft.configuredAt === undefined
                        ? undefined
                        : new Date(draft.configuredAt).toISOString()
                    }
                    data-testid="agent-guardrails-draft-configured-at"
                  >
                    {labels.dateTime(draft.configuredAt) ?? empty}
                  </time>
                </Row>
              </dl>
            )}
          </div>
          <div
            className="min-w-0 rounded-lg border border-border bg-card p-4"
            data-testid="agent-guardrails-published"
          >
            <p className="text-sm font-medium">
              {t('guardrails.draftPublished.published')}
            </p>
            {latest === null ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {t('guardrails.draftPublished.emptyPublished')}
              </p>
            ) : (
              <dl
                className={GRID_CLASS}
                data-testid="agent-guardrails-published-values"
                data-published-provenance={immutableProvenance(latest)}
              >
                <Row label={t('guardrails.draftPublished.version')}>
                  {t('labels.effective.published', { version: latest.version })}
                </Row>
                <Row label={t('guardrails.draftPublished.scope')}>
                  {labels.scope(latest.scope)} · {t(`phases.${latest.phase}`)}
                </Row>
                <Row label={t('guardrails.draftPublished.autonomy')}>
                  {latest.autonomyRung === undefined
                    ? t('labels.provenance.INHERITED')
                    : labels.rung(latest.autonomyRung)}
                </Row>
                <Row label={t('guardrails.draftPublished.money')}>
                  {money(
                    latest.currency,
                    latest.costCeilingPerRunMinor,
                    latest.costCeilingPerPhaseMinor
                  )}
                </Row>
                <Row label={t('guardrails.draftPublished.review')}>
                  <span data-testid="agent-guardrails-published-review">
                    {labels.reviewPolicy(latest.reviewPolicy)}
                  </span>
                </Row>
                <Row label={t('guardrails.draftPublished.outputs')}>
                  {outputs(latest.outputPermissions)}
                </Row>
                <Row label={t('guardrails.draftPublished.reason')}>
                  {latest.clientOverrideReason ?? empty}
                </Row>
                <Row label={t('guardrails.draftPublished.publisher')}>
                  {latest.publishedByName ?? latest.publishedBy}
                </Row>
                <Row label={t('guardrails.draftPublished.publishedAt')}>
                  <time
                    dateTime={new Date(latest.publishedAt).toISOString()}
                    data-testid="agent-guardrails-published-at"
                  >
                    {labels.dateTime(latest.publishedAt) ?? empty}
                  </time>
                </Row>
                <Row label={t('guardrails.draftPublished.digest')}>
                  <span
                    className="block truncate font-mono text-xs"
                    title={latest.payloadDigest}
                  >
                    {latest.payloadDigest}
                  </span>
                </Row>
              </dl>
            )}
          </div>
        </div>
      </section>
      <AgentSettingsForm
        settingsKey={settingsKey}
        agent={agent}
        outputPermissions={policy?.outputPermissions}
        onDirtyChange={setHasUnsavedEdits}
      />
      <AgentContextPolicyPanel
        policy={policy}
        loading={policyLoading}
        error={policyError}
        onRetry={onPolicyRetry}
      />
      <AgentDryRunPanel
        settingsKey={settingsKey}
        hasUnsavedEdits={hasUnsavedEdits}
      />
      <AgentVersionHistory
        versions={versions}
        selectedVersion={selectedVersion}
        onChange={setSelectedVersion}
      />
    </>
  )
}
